<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12">

                <!-- Header sticky -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3 sticky-header">
                    <div class="d-flex align-center">
                        <!-- Sinistra: export PDF / Excel -->
                        <div class="d-flex align-center">
                            <v-btn v-if="isCoordAss && !filePdfNew"
                                   icon color="pink" :loading="loadingPdfNew"
                                   title="Esporta elenco oratori PDF"
                                   @click="elencoPdfNew">
                                <v-icon>mdi-file-pdf-box</v-icon>
                            </v-btn>
                            <v-btn v-if="isCoordAss && filePdfNew"
                                   icon color="error" :loading="loadingPdfNew"
                                   @click="visualizzaPdfNew">
                                <v-icon>mdi-magnify</v-icon>
                            </v-btn>
                            <v-btn v-if="isCoordAss && !fileExcelCam"
                                   icon color="teal" :loading="loadingExcelCam"
                                   @click="elencoExcelCam"
                                   title="Esporta formato ORATORI PUBBLICI ESTERNI">
                                <v-icon>mdi-file-excel-outline</v-icon>
                            </v-btn>
                            <v-btn v-if="isCoordAss && fileExcelCam"
                                   icon color="teal" :loading="loadingExcelCam"
                                   @click="visualizzaExcelCam">
                                <v-icon>mdi-magnify</v-icon>
                            </v-btn>
                        </div>
                        <!-- Centro: titolo -->
                        <div class="flex-grow-1">
                            <div class="text-subtitle-2 font-weight-bold primary--text text-center">
                                Circoscrizione {{ nomeCircoscrizione }}
                            </div>
                        </div>
                        <!-- Destra: NWS export + aggiungi -->
                        <div class="d-flex align-center">
                            <v-btn v-if="isAdmin || $store.state.oratore.isAssistente" icon :loading="loadingCsvCong"
                                   @click="dialogNws = true">
                                <img src="@/assets/nws_logo.webp" width="18" height="18" style="border-radius:4px;background:linear-gradient(rgb(50,143,196),rgb(78,197,212));padding:2px" />
                            </v-btn>
                            <v-btn v-if="isAdmin" icon color="primary" @click="nuovaCongregazione">
                                <v-icon>mdi-view-grid-plus</v-icon>
                            </v-btn>
                        </div>
                    </div>
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Cerca congregazione..."
                        outlined rounded dense hide-details clearable
                        class="mt-2"
                    />
                </v-card>

                <!-- Skeleton -->
                <template v-if="loading">
                    <v-card v-for="n in 5" :key="n" class="rounded-xl elevation-1 mb-2 px-3 py-2">
                        <v-skeleton-loader type="list-item-two-line" />
                    </v-card>
                </template>

                <!-- Lista congregazioni -->
                <template v-else>
                    <v-card v-for="item in congregazioniFiltrate" :key="item.congregazione.id"
                            class="rounded-xl mb-2 cong-card"
                            :elevation="isSelectedOnTablet(item) ? 0 : 1"
                            :class="[isPropriaCongreg(item) ? 'card-cliccabile' : '', isSelectedOnTablet(item) ? 'cong-selected' : '']"
                            @click="clickCongregazione(item)">
                        <div class="d-flex align-center px-3 pt-3 pb-1">
                            <!-- Icona -->
                            <v-avatar size="38" color="primary" class="mr-3 flex-shrink-0" rounded>
                                <v-icon dark size="20">mdi-home-city-outline</v-icon>
                            </v-avatar>

                            <!-- Contenuto principale -->
                            <div class="flex-grow-1" style="min-width:0">
                                <div class="d-flex align-center">
                                    <span class="text-body-2 font-weight-bold">{{ item.congregazione.nome }}</span>
                                    <v-chip v-if="item.numeroOratori > 0" x-small class="ml-2 oratori-badge">
                                        {{ item.numeroOratori }} oratori esterni
                                    </v-chip>
                                </div>
                                <div v-if="item.congregazione.indirizzo" class="text-caption grey--text d-flex align-center">
                                    <v-icon x-small color="grey" class="mr-1">mdi-map-marker-outline</v-icon>
                                    <span>{{ item.congregazione.indirizzo }}</span>
                                    <v-icon v-if="item.congregazione.coordinate"
                                        x-small color="primary" class="ml-1" style="cursor:pointer"
                                        @click.stop="$openUrl('https://maps.google.it/?q=' + item.congregazione.coordinate)">
                                        mdi-open-in-new
                                    </v-icon>
                                </div>
                                <div v-if="item.congregazione.giorno" class="text-caption grey--text">
                                    <v-icon x-small color="grey" class="mr-1">mdi-calendar-clock-outline</v-icon>
                                    {{ item.congregazione.giorno }} alle {{ item.congregazione.ora }}
                                </div>
                            </div>

                            <!-- Azioni -->
                            <div class="d-flex flex-column align-end ml-2 flex-shrink-0" style="gap:4px">
                                <v-btn v-if="modificheAbilitate(item)"
                                       icon x-small color="primary"
                                       @click.stop="apriOratori(item)">
                                    <v-icon small>mdi-account-multiple-outline</v-icon>
                                </v-btn>
                                <v-btn v-if="modificheAbilitate(item)"
                                       icon x-small color="grey"
                                       @click.stop="apriCongregazione(item)">
                                    <v-icon small>mdi-pencil-outline</v-icon>
                                </v-btn>
                                <!-- Freccia per non-privilegiati sulla propria congregazione -->
                                <v-icon v-if="!modificheAbilitate(item) && isPropriaCongreg(item)"
                                        color="primary" size="18">mdi-chevron-right</v-icon>
                            </div>
                        </div>

                        <!-- Coordinatore -->
                        <div v-if="item.congregazione.coordinatore" class="px-3 pb-2 mt-1">
                            <v-divider class="mb-2" />
                            <div class="d-flex align-center flex-wrap" style="gap:4px">
                                <v-icon x-small color="grey" class="mr-1">mdi-account-tie-outline</v-icon>
                                <span class="text-caption font-weight-medium">{{ item.congregazione.coordinatore.nominativo }}</span>
                                <span v-if="item.congregazione.coordinatore.telefono"
                                      class="text-caption grey--text ml-1 contact-link d-flex align-center"
                                      @click.stop="openTel(item.congregazione.coordinatore.telefono)">
                                    <v-icon x-small color="primary" class="mr-1">mdi-phone-outline</v-icon>{{ item.congregazione.coordinatore.telefono }}
                                </span>
                                <v-chip v-if="item.congregazione.coordinatore.isAnziano"      x-small dark color="teal" class="ml-1">A</v-chip>
                                <v-chip v-if="item.congregazione.coordinatore.isSM"           x-small dark color="blue darken-1" class="ml-1">SM</v-chip>
                                <v-chip v-if="item.congregazione.coordinatore.isAssistente"   x-small dark color="grey darken-2" class="ml-1">ASSISTENTE</v-chip>
                                <v-chip v-if="item.congregazione.coordinatore.isCoordinatore" x-small dark color="deep-orange darken-4" class="ml-1">ADM</v-chip>
                                <v-chip v-if="item.congregazione.coordinatore.isAdmin"        x-small dark color="orange darken-1" class="ml-1">APP-ADM</v-chip>
                                <chip-login-oratore :oratore="item.congregazione.coordinatore" class="ml-1" />
                            </div>
                        </div>
                    </v-card>

                    <div v-if="congregazioniFiltrate.length === 0" class="text-center grey--text py-10">
                        <v-icon large color="grey lighten-1">mdi-home-off-outline</v-icon>
                        <div class="mt-2 text-body-2">Nessuna congregazione trovata</div>
                    </div>
                </template>

            </v-col>
        </v-row>

        <!-- Dialog NWS Export -->
        <v-dialog v-model="dialogNws" max-width="440">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center" style="gap:8px">
                    <img src="@/assets/nws_logo.webp" width="22" height="22" style="border-radius:5px;background:linear-gradient(rgb(50,143,196),rgb(78,197,212));padding:2px" />
                    Esportazione per NW Scheduler
                    <v-chip x-small color="orange" dark class="ml-1" style="font-size:10px">BETA</v-chip>
                </v-card-title>
                <v-card-text class="text-body-2">
                    <div class="mb-2">Verranno prodotti file CSV da caricare in NW Scheduler.</div>
                    <div class="mb-1 font-weight-medium">Si consiglia di esportare e caricare nell'ordine:</div>
                    <ol class="pl-4 mb-3" style="line-height:1.8">
                        <li class="primary--text font-weight-bold">Congregazioni: unico file CSV &nbsp;<v-chip x-small color="primary" dark>questa esportazione</v-chip></li>
                        <li><strong>Oratori:</strong> file ZIP con un CSV per ogni congregazione <span class="grey--text">(il sw non consente un caricamento massivo)</span></li>
                        <li><strong>Programma Interno:</strong> unico file CSV. Su NW Scheduler indicare <code>dd/mm/yyyy</code> come formato data</li>
                    </ol>
                    <v-alert dense text type="info" class="text-caption mb-0">
                        Si consiglia di fare alcune prove su NW Scheduler con porzioni dei file generati.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogNws = false">Annulla</v-btn>
                    <v-btn color="primary" depressed @click="dialogNws = false; esportaCsvCongregazioni()">
                        <v-icon left small>mdi-download</v-icon>Esporta
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { callService, callPost, euro, gLocalData, gCheckData, gServerData, gDataCalendar, Snack, SUPERADMIN_CODE } from '@/modules/utilities.js'
import { bus } from '@/main.js'
import ChipLoginOratore from '@/components/ChipLoginOratore.vue'

export default {
    components: { ChipLoginOratore },
    inject: {
        tabletState:      { default: null },
        setDetailCong:    { default: null },
        setDetailOratori: { default: null },
    },
    data: () => ({
        congregazioniEstese: [],
        congregazione: {},
        oratoriEstesi: [],
        selectedItem: [],
        oratore: {},
        discorsi: [],
        listaDiscorsi: [],
        loadingOratori: false,
        loading: false,
        _loadedForCirc: null,
        loadingPdf: false,
        filePdf: '',
        loadingPdfNew: false,
        filePdfNew: '',
        loadingExcel: false,
        fileExcel: '',
        loadingExcelCam: false,
        fileExcelCam: '',
        loadingCsvCong: false,
        dialogNws: false,
        SUPERADMIN_CODE: 0,
        search: '',
        prenotazioniAttive: false,
    }),
    computed: {
        nomeCircoscrizione() {
            return this.$store.state.circoscrizione?.nome || '';
        },
        isAdmin() {
            return this.$store.state.oratore.codice == this.SUPERADMIN_CODE || this.$store.state.oratore.isAdmin;
        },
        isSuperAdmin() {
            return this.$store.state.oratore.codice == this.SUPERADMIN_CODE;
        },
        isCoordAss() {
            const o = this.$store.state.oratore;
            return o.codice == this.SUPERADMIN_CODE || o.isAdmin || o.isCoordinatore || o.isAssistente;
        },
        isTablet() {
            return this.$vuetify.breakpoint.mdAndUp;
        },
        congregazioniFiltrate() {
            if (!this.search) return this.congregazioniEstese;
            const s = this.search.toLowerCase();
            return this.congregazioniEstese.filter(item =>
                (item.congregazione.nome && item.congregazione.nome.toLowerCase().includes(s)) ||
                (item.congregazione.indirizzo && item.congregazione.indirizzo.toLowerCase().includes(s))
            );
        }
    },
    methods: {
        async caricaDati() {
            this.loading = true;
            this.prenotazioniAttive = this.$store.state.circoscrizione?.prenotazione || false;
            this.congregazione.prenotazione = this.prenotazioniAttive;
            const param = [this.$store.state.idCircoscrizione];
            this.congregazioniEstese = await callService("/api/congregazione", param);
            this.listaDiscorsi = await callService("/api/discorso", param);
            this._loadedForCirc = this.$store.state.idCircoscrizione;
            this.loading = false;
        },
        customFilter(value, search, item) {
            if (!search) return true;
            return item.congregazione.nome.toLowerCase().includes(search.toLowerCase());
        },
        modificheAbilitate(item) {
            const o = this.$store.state.oratore;
            if (o.codice == this.SUPERADMIN_CODE || o.isAdmin) return true;
            return o.congregazione && o.congregazione.id == item.congregazione.id && (o.isCoordinatore || o.isAssistente);
        },
        isPropriaCongreg(item) {
            const o = this.$store.state.oratore;
            if (o.codice == this.SUPERADMIN_CODE || o.isAdmin || o.isCoordinatore || o.isAssistente) return true;
            return o.congregazione && o.congregazione.id == item.congregazione.id;
        },
        clickCongregazione(item) {
            if (!this.isPropriaCongreg(item)) return;
            if (this.isTablet && this.setDetailOratori) {
                this.setDetailOratori(item.congregazione.id, item.congregazione.nome);
            } else {
                this.$router.push({
                    name: 'oratori-congregazione',
                    params: { idCongregazione: item.congregazione.id, nome: item.congregazione.nome }
                });
            }
        },
        isSelectedOnTablet(item) {
            if (!this.isTablet || !this.tabletState) return false;
            const id = String(item.congregazione.id);
            return this.tabletState.detailCongId === id ||
                   this.tabletState.detailOratoriId === id;
        },
        nuovaCongregazione() {
            if (this.isTablet && this.setDetailCong) {
                this.setDetailCong('0');
            } else {
                this.$router.push({ name: "congregazione", params: { id: "0" } });
            }
        },
        async apriCongregazione(item) {
            if (this.isTablet && this.setDetailCong) {
                this.setDetailCong(String(item.congregazione.id));
            } else {
                this.$router.push({ name: "congregazione", params: { id: item.congregazione.id } });
            }
        },
        async apriOratori(item) {
            if (this.isTablet && this.setDetailOratori) {
                this.setDetailOratori(item.congregazione.id, item.congregazione.nome);
            } else {
                this.$router.push({
                    name: "oratori-congregazione",
                    params: { idCongregazione: item.congregazione.id, nome: item.congregazione.nome }
                });
            }
        },
        async selezionaCong(item) {
            this.$store.state.idCongregazione = item.congregazione.id;
            this.$store.state.congregazioneCorrente = item.congregazione;
            this.$store.state.externalCirc = true;
            this.$store.state.externalCong = true;
            this.$router.replace({ name: "programma" });
        },
        async onUpdateOratore({ idOra, idCong, idCongUpd }) {
            const i = this.oratoriEstesi.findIndex(o => o.oratore.id == idOra);
            if (i >= 0) {
                const qs = [idCong ? `idCong=${idCong}` : '', idOra ? `idOra=${idOra}` : ''].filter(Boolean).join('&');
                const oraExt = await callService(`/api/oratore/congregazioneOratore${qs ? '?' + qs : ''}`, [this.$store.state.idCircoscrizione, !!idCong]);
                this.oratoriEstesi.splice(i, 1, oraExt[0]);
            }
        },
        async realodCongregazione(congregazione) {
            const i = this.congregazioniEstese.findIndex(ce => ce.congregazione.id == congregazione.id);
            if (i > -1) {
                this.congregazioniEstese[i].congregazione = congregazione;
            } else {
                this.loading = true;
                const param = [this.$store.state.idCircoscrizione];
                this.congregazioniEstese = await callService("/api/congregazione", param);
                this.loading = false;
            }
        },
        async esportaCsvCongregazioni() {
            this.loadingCsvCong = true;
            const out = await callService('/api/congregazione/exportCsv', [this.$store.state.idCircoscrizione]);
            if (out && out.fileOutput) {
                const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + out.fileOutput;
                const filename = out.fileOutput.split('/').pop();
                if (window.HybridWebView) {
                    this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
                } else {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.target = '_blank';
                    a.click();
                }
            }
            this.loadingCsvCong = false;
        },
        async elencoPdf() {
            this.loadingPdf = true;
            const param = [this.$store.state.idCircoscrizione];
            const outputPdf = await callService("/api/print/elencoPdf", param);
            if (outputPdf) this.filePdf = outputPdf.fileOutput;
            this.loadingPdf = false;
        },
        async visualizzaPdf() {
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + this.filePdf;
            const filename = this.filePdf.split('/').pop().replace(/_[0-9a-f]{4}\./, '.');
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.target = '_blank';
                a.click();
            }
            this.filePdf = '';
        },
        async elencoPdfNew() {
            this.loadingPdfNew = true;
            const param = [this.$store.state.idCircoscrizione];
            const output = await callService("/api/print/elencoPdfNew", param);
            if (output) this.filePdfNew = output.fileOutput;
            this.loadingPdfNew = false;
        },
        async visualizzaPdfNew() {
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + this.filePdfNew;
            const filename = this.filePdfNew.split('/').pop();
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.target = '_blank';
                a.click();
            }
            this.filePdfNew = '';
        },
        async elencoExcel() {
            this.loadingExcel = true;
            const param = [this.$store.state.idCircoscrizione];
            const outputExcel = await callService("/api/print/elencoExcel", param);
            if (outputExcel) this.fileExcel = outputExcel.fileOutput;
            this.loadingExcel = false;
        },
        async elencoExcelCam() {
            this.loadingExcelCam = true;
            const param = [this.$store.state.idCircoscrizione];
            const outputExcel = await callService("/api/print/elencoExcelCam", param);
            if (outputExcel) this.fileExcelCam = outputExcel.fileOutput;
            this.loadingExcelCam = false;
        },
        async visualizzaExcelCam() {
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + this.fileExcelCam;
            const filename = this.fileExcelCam.split('/').pop();
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.target = '_blank';
                a.click();
            }
            this.fileExcelCam = '';
        },
        async visualizzaExcel() {
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + this.fileExcel;
            const filename = this.fileExcel.split('/').pop().replace(/_[0-9a-f]{4}\./, '.');
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.target = '_blank';
                a.click();
            }
            this.fileExcel = '';
        },
        openTel(tel) {
            if (tel) this.$openUrl('tel:' + tel.replace(/\s/g, ''));
        },
        async ricaricaSilente() {
            const param = [this.$store.state.idCircoscrizione];
            this.congregazioniEstese = await callService("/api/congregazione", param);
        },
    },
    watch: {
        '$updaterHub.signals.congregazioni'() {
            this.ricaricaSilente();
        },
        '$updaterHub.signals.oratori'() {
            this.ricaricaSilente();
        },
    },
    async created() {
        this.SUPERADMIN_CODE = SUPERADMIN_CODE;
        bus.$on('dialog-update-oratore', this.onUpdateOratore);
        bus.$on("congregazioni-reload", this.realodCongregazione);
        await this.caricaDati();
    },
    async activated() {
        const circAttuale = this.$store.state.idCircoscrizione;
        if (circAttuale !== this._loadedForCirc) {
            await this.caricaDati();
        }
    },
    beforeDestroy() {
        bus.$off('dialog-update-oratore', this.onUpdateOratore);
        bus.$off("congregazioni-reload", this.realodCongregazione);
    }
}
</script>

<style scoped>
.sticky-header {
    position: sticky;
    top: 0;
    z-index: 4;
}
.cong-card {
    cursor: default;
}
.contact-link {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-color: rgba(0,0,0,0.25);
    text-underline-offset: 2px;
}
.card-cliccabile {
    cursor: pointer;
}
.cong-selected {
    border: 2px solid var(--v-primary-base) !important;
    background-color: rgba(25, 118, 210, 0.06) !important;
}
.theme--dark .cong-selected {
    background-color: rgba(144, 202, 249, 0.08) !important;
}
.oratori-badge {
    font-size: 0.65rem;
    font-weight: 700;
    background-color: rgba(25, 118, 210, 0.1) !important;
    color: #1976d2 !important;
}
.theme--dark .oratori-badge {
    background-color: rgba(144, 202, 249, 0.15) !important;
    color: #90caf9 !important;
}
</style>
